import { AlertMessage } from '@/app/components'
import { Grid, Text } from '@/components'
import { useAppDispatch, useAppSelector } from '@/hooks'
import { thermostatComfortSettingsCollection } from '@/reducers/thermostatComfortSettings'
import { Thermostat, ThermostatComfortSetting } from '@/types'
import { ID } from '@/types/model'
import { debounce } from '@/utils/limit'
import { useCallback, useMemo, useState } from 'react'
import EventSetpointStepper from '../EventSetpointStepper'

// Absolute setpoint bounds the steppers allow, mirroring the thermostat's
// supported temperature range.
const MIN_TEMP_SETTING = 50
const MAX_TEMP_SETTING = 90

type Props = {
  comfortSettings: ThermostatComfortSetting[]
  thermostatId: Thermostat['id']
  onChange?: (comfortSettings: ThermostatComfortSetting[]) => void
  // Whether to render the built-in instructional copy above the steppers. Off
  // when the host page (e.g. Event Temperatures) renders its own intro.
  showInstructions?: boolean
}

export default function ComfortSettingSlideEditor(props: Props) {
  const dispatch = useAppDispatch()
  const comfortSettingsUpdateError = useAppSelector((state) =>
    thermostatComfortSettingsCollection.selectors.queryState
      .select(state, {
        method: 'PATCH',
      })
      .some((queryState) => Boolean(queryState.error)),
  )
  // used to remount the steppers if updating failed -> resets to last valid value
  const [resetKey, setResetKey] = useState(false)

  const performUpdate = useCallback(
    async (
      settingId: ID,
      updatedSetting: Partial<ThermostatComfortSetting>,
    ) => {
      const promise = await dispatch(
        thermostatComfortSettingsCollection.actions.update(
          `/thermostats/${props.thermostatId}/comfort_settings/${settingId}/`,
          updatedSetting,
        ),
      )
      if (promise.type.includes('UPDATE_FAILURE')) {
        setResetKey((prev) => !prev)
      }
      props.onChange?.(props.comfortSettings)
    },
    [dispatch, props.thermostatId, props.comfortSettings, props.onChange],
  )

  // A separate debounced dispatcher per comfort setting AND per heat/cool field,
  // so concurrent edits to different steppers don't clobber each other.
  const debouncedUpdaters = useMemo(() => {
    const updaters: Record<
      string,
      (updatedSetting: Partial<ThermostatComfortSetting>) => void
    > = {}
    for (const comfortSetting of props.comfortSettings) {
      for (const field of ['heat', 'cool'] as const) {
        updaters[`${comfortSetting.id}:${field}`] = debounce(
          (updatedSetting: Partial<ThermostatComfortSetting>) =>
            performUpdate(comfortSetting.id, updatedSetting),
          300,
        )
      }
    }
    return updaters
  }, [props.comfortSettings, performUpdate])

  return (
    <Grid flow="row" gap="32px">
      <AlertMessage variant="error" show={Boolean(comfortSettingsUpdateError)}>
        There was an issue updating your comfort settings. Please try again
        later.
      </AlertMessage>
      {(props.showInstructions ?? true) && (
        <Text variant="body2">
          What should we shift your temperature to during an event? Choose a
          target for each of your schedules.
        </Text>
      )}
      <div className="flex flex-col">
        {props.comfortSettings.map((comfortSetting, index) => {
          const defaultDelta = comfortSetting.event_default_setpoint_delta ?? 1
          // Event heating setpoint is at or below the scheduled heat setpoint.
          const heatInitialValue =
            comfortSetting.event_heat_setpoint_with_defaults ??
            comfortSetting.heat_setpoint - defaultDelta
          // Event cooling setpoint is at or above the scheduled cool setpoint.
          const coolInitialValue =
            comfortSetting.event_cool_setpoint_with_defaults ??
            comfortSetting.cool_setpoint + defaultDelta

          const onHeatChange = (value: number) => {
            // NumberInput fires onChange with the initial value on mount; skip no-ops.
            if (value === heatInitialValue) return
            debouncedUpdaters[`${comfortSetting.id}:heat`]({
              event_heat_setpoint_delta: comfortSetting.heat_setpoint - value,
            })
          }
          const onCoolChange = (value: number) => {
            if (value === coolInitialValue) return
            debouncedUpdaters[`${comfortSetting.id}:cool`]({
              event_cool_setpoint_delta: value - comfortSetting.cool_setpoint,
            })
          }

          return (
            <div
              key={comfortSetting.id}
              className={
                index > 0
                  ? 'mt-6 border-t border-solid border-base-grey-200 pt-6'
                  : undefined
              }
            >
              <Text variant="subheader" className="pb-6">
                {comfortSetting.name}
              </Text>
              <div className="flex flex-col gap-4">
                <EventSetpointStepper
                  key={`heat-${Number(resetKey)}`}
                  variant="heat"
                  title="Heating"
                  initialValue={heatInitialValue}
                  range={[MIN_TEMP_SETTING, comfortSetting.heat_setpoint - 1]}
                  onChange={onHeatChange}
                  helperText={`(Normally ${comfortSetting.heat_setpoint}°)`}
                />
                <EventSetpointStepper
                  key={`cool-${Number(resetKey)}`}
                  variant="cool"
                  title="Cooling"
                  initialValue={coolInitialValue}
                  range={[comfortSetting.cool_setpoint + 1, MAX_TEMP_SETTING]}
                  onChange={onCoolChange}
                  helperText={`(Normally ${comfortSetting.cool_setpoint}°)`}
                />
              </div>
            </div>
          )
        })}
      </div>
    </Grid>
  )
}
